import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Newspaper, Pin, Sparkles, RotateCw, Check, Minus } from "lucide-react";
import { api } from "../lib/api";
import WimArticleCard from "../components/shared/WimArticleCard";
import ArticleBody from "../components/shared/ArticleBody";
import ArticleCTA from "../components/shared/ArticleCTA";
import SEOHead from "../components/shared/SEOHead";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [news, setNews] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | not-found | error
  const [nonce, setNonce] = useState(0);

  const fetchPost = useCallback((signal) => {
    setStatus("loading");
    setPost(null);
    return api
      .get(`/api/blog/${slug}`, { signal })
      .then((r) => {
        setPost(r.data);
        setStatus("ok");
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        if (err?.response?.status === 404) {
          setStatus("not-found");
        } else {
          setStatus("error");
        }
      });
  }, [slug]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchPost(ctrl.signal);
    api.get(`/api/news`, { signal: ctrl.signal })
      .then((r) => setNews((r.data.items || []).slice(0, 2)))
      .catch(() => {});
    return () => ctrl.abort();
  }, [fetchPost, nonce]);

  // bfcache restore re-fetch so a stale "Loading…" state never lingers
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setNonce((n) => n + 1);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Load Related Articles when the post is loaded — prefer same category, then
  // back-fill with most recent posts. Always excludes the current article.
  useEffect(() => {
    if (!post) return;
    const sameCat = post.category
      ? api
          .get("/api/blog", { params: { category: post.category } })
          .then((r) => r.data.posts || [])
      : Promise.resolve([]);
    const allRecent = api.get("/api/blog").then((r) => r.data.posts || []);
    Promise.all([sameCat, allRecent]).then(([catList, allList]) => {
      const merged = [];
      const seen = new Set([post.slug]);
      for (const p of [...catList, ...allList]) {
        if (seen.has(p.slug)) continue;
        seen.add(p.slug);
        merged.push(p);
        if (merged.length >= 3) break;
      }
      setRelated(merged);
    }).catch(() => {});
  }, [post]);

  if (status === "not-found") {
    return (
      <div className="container-jcc py-24 text-center">
        <SEOHead title="Article not found" path={`/wim/${slug}`} />
        <h1 className="font-display text-3xl font-bold">Article not found</h1>
        <p className="mt-3 text-ink-muted">This article may have been moved or unpublished.</p>
        <Link to="/wim-blog" className="btn-outline mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Back to the WIM Blog
        </Link>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="container-jcc py-24 max-w-xl text-center">
        <SEOHead title="Couldn't load article" path={`/wim/${slug}`} />
        <h1 className="font-display text-3xl font-bold">We couldn't load this article</h1>
        <p className="mt-3 text-ink-muted">
          Your connection may have dropped. Please try again.
        </p>
        <div className="mt-6 flex gap-3 justify-center">
          <button
            type="button"
            onClick={() => setNonce((n) => n + 1)}
            className="btn-teal inline-flex"
            data-testid="blog-post-retry"
          >
            <RotateCw className="w-4 h-4" /> Try again
          </button>
          <Link to="/wim-blog" className="btn-outline inline-flex">
            <ArrowLeft className="w-4 h-4" /> Back to WIM Blog
          </Link>
        </div>
      </div>
    );
  }

  if (status === "loading" || !post) {
    return (
      <div className="container-jcc py-24 text-ink-muted text-center" data-testid="blog-post-loading">
        Loading…
      </div>
    );
  }

  const metaDescription =
    post.excerpt ||
    (post.body ? String(post.body).slice(0, 158).replace(/\s+\S*$/, "") + "…" : "");

  const articleJsonLd = {
    "@type": "Article",
    headline: post.title,
    description: metaDescription,
    articleSection: post.category,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: { "@type": "Organization", name: post.author || "Just Care Community" },
    publisher: {
      "@type": "Organization",
      name: "Just Care Community",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${typeof window !== "undefined" ? window.location.origin : ""}/wim/${post.slug}`,
    },
    image: post.featured_image || undefined,
  };

  // When the article carries a FAQ block we extend the structured data with
  // a FAQPage entity so search engines can surface the Q&A directly.
  const faqJsonLd = Array.isArray(post.faq_items) && post.faq_items.length
    ? {
        "@type": "FAQPage",
        mainEntity: post.faq_items.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      }
    : null;

  const seoJsonLd = faqJsonLd
    ? { "@context": "https://schema.org", "@graph": [articleJsonLd, faqJsonLd] }
    : { "@context": "https://schema.org", ...articleJsonLd };

  return (
    <article className="bg-white" data-testid="page-blog-post">
      <SEOHead
        title={post.title}
        description={metaDescription}
        path={`/wim/${post.slug}`}
        type="article"
        image={post.featured_image}
        jsonLd={seoJsonLd}
      />

      <div className="container-jcc pt-8 md:pt-12 max-w-3xl">
        <Link
          to="/wim-blog"
          className="btn-outline !px-4 !py-2 text-sm"
          data-testid="back-to-wim"
        >
          <ArrowLeft className="w-4 h-4" /> Back to WIM Blog
        </Link>
      </div>

      <header className="container-jcc max-w-3xl pt-6 pb-2">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="pill bg-teal-50 text-teal">{post.category}</span>
          {post.is_featured && (
            <span
              className="pill bg-purple text-white flex items-center gap-1.5"
              data-testid="article-featured-pill"
            >
              <Sparkles className="w-3.5 h-3.5" /> Editor's Pick
            </span>
          )}
          {post.read_minutes ? (
            <span className="text-sm text-ink-soft flex items-center gap-1">
              <Clock className="w-4 h-4" /> {post.read_minutes} min read
            </span>
          ) : null}
          {post.author ? (
            <span className="text-sm text-ink-soft">· {post.author}</span>
          ) : null}
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-[1.1]">
          {post.title}
        </h1>
        {post.excerpt && (
          <p className="mt-4 text-lg text-ink-muted leading-relaxed">{post.excerpt}</p>
        )}
      </header>

      {/* Featured image (or branded placeholder) */}
      <div className="container-jcc max-w-4xl mt-8">
        <div className="aspect-[16/7] rounded-3xl bg-gradient-to-br from-teal to-purple overflow-hidden">
          {post.featured_image ? (
            <img
              src={post.featured_image}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : null}
        </div>
      </div>

      {/* Pinned-article callout — only renders for editor-curated, pinned
          pieces. Visually distinct (teal/purple accent) but stays inside the
          existing JCC typography and palette so it doesn't break long-form
          rhythm. */}
      {post.pinned_note && (
        <aside
          className="container-jcc max-w-3xl mt-10"
          data-testid="article-pinned-note"
        >
          <div className="relative rounded-2xl border border-purple/20 bg-gradient-to-br from-purple/[0.04] to-teal/[0.04] p-6 md:p-8">
            <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 pill bg-purple text-white shadow-sm">
              <Pin className="w-3.5 h-3.5" /> Why this article is pinned
            </div>
            <p className="text-ink leading-relaxed text-base md:text-lg mt-2">
              {post.pinned_note}
            </p>
          </div>
        </aside>
      )}

      {/* Article body */}
      <div className="container-jcc max-w-3xl mt-10 text-ink">
        <ArticleBody body={post.body} testId="article-body" />
      </div>

      {/* Optional tool CTA — only renders when the article carries a
          tool_cta block, e.g. linking to a member-only workspace tool
          such as the Tax Confidence Kit. */}
      {post.tool_cta?.to && (
        <div className="container-jcc max-w-4xl mt-12" data-testid="article-tool-cta">
          <ArticleCTA
            eyebrow={post.tool_cta.eyebrow}
            title={post.tool_cta.title}
            description={post.tool_cta.description}
            primary={{ label: post.tool_cta.label, to: post.tool_cta.to, accent: "purple" }}
            secondary={null}
          />
        </div>
      )}

      {/* Optional comparison table — only renders when supplied via the
          comparison_table field. Designed to feel native to the article rhythm
          rather than slot in as a generic data table. */}
      {post.comparison_table?.headers?.length && post.comparison_table?.rows?.length ? (
        <section
          className="container-jcc max-w-3xl mt-12"
          data-testid="article-comparison"
        >
          <div className="rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm md:text-base text-left">
              <thead className="bg-surface">
                <tr>
                  {post.comparison_table.headers.map((h, i) => (
                    <th
                      key={i}
                      className={`p-4 font-display font-semibold align-bottom break-words ${
                        i === 0 ? "text-ink" : i === post.comparison_table.headers.length - 1
                          ? "text-teal"
                          : "text-ink-muted"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {post.comparison_table.rows.map((row, ri) => (
                  <tr key={ri} className="border-t border-slate-200">
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className={`p-4 align-top break-words ${
                          ci === 0 ? "font-medium text-ink" : "text-ink-muted"
                        } ${
                          ci === row.length - 1 ? "bg-teal/[0.03] text-ink" : ""
                        }`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* Optional FAQ accordion — only renders when supplied. Pairs with
          the FAQPage JSON-LD injected via SEOHead above. */}
      {Array.isArray(post.faq_items) && post.faq_items.length > 0 ? (
        <section
          className="container-jcc max-w-3xl mt-14"
          data-testid="article-faq"
        >
          <div className="overline text-teal mb-2">Frequently asked questions</div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold leading-tight mb-6">
            Quick answers
          </h2>
          <Accordion type="single" collapsible className="space-y-2">
            {post.faq_items.map((it, i) => (
              <AccordionItem
                value={`faq-${i}`}
                key={i}
                className="border-b border-slate-200"
              >
                <AccordionTrigger
                  className="text-left font-display font-medium text-base md:text-lg hover:no-underline"
                  data-testid={`article-faq-q-${i}`}
                >
                  {it.q}
                </AccordionTrigger>
                <AccordionContent
                  className="text-ink-muted leading-relaxed text-base pb-5 whitespace-pre-wrap"
                  data-testid={`article-faq-a-${i}`}
                >
                  {it.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ) : null}

      {/* Bottom return-to-index button — visible on desktop & mobile so readers
          always have a clear way back to the WIM Blog from the article. */}
      <div className="container-jcc max-w-3xl mt-10 flex justify-center md:justify-start">
        <Link
          to="/wim-blog"
          className="btn-outline"
          data-testid="back-to-wim-bottom"
        >
          <ArrowLeft className="w-4 h-4" /> Back to all WIM Blog articles
        </Link>
      </div>

      {/* Reusable CTA */}
      <div className="container-jcc max-w-4xl">
        <ArticleCTA />
      </div>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="container-jcc max-w-7xl mt-16" data-testid="related-articles">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-teal mb-2">Related from the WIM Blog</div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold leading-snug">
                Keep reading
              </h2>
            </div>
            <Link to="/wim-blog" className="btn-ghost text-sm hidden md:inline-flex">
              All articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p) => (
              <WimArticleCard key={p.slug} post={p} testIdPrefix="related-card" />
            ))}
          </div>
        </section>
      )}

      {/* Cross-link to Care News (kept from previous behaviour) */}
      {news.length > 0 && (
        <section
          className="container-jcc max-w-4xl mt-16 mb-24"
          data-testid="wim-post-related-news"
        >
          <div className="rounded-2xl border border-slate-200 bg-surface p-6 md:p-8">
            <div className="overline text-teal mb-3 flex items-center gap-2">
              <Newspaper className="w-4 h-4" /> Recent sector updates from Care News
            </div>
            <ul className="space-y-3">
              {news.map((n) => (
                <li key={n.slug}>
                  <Link
                    to={`/care-news/${n.slug}`}
                    className="flex items-center justify-between gap-4 group"
                  >
                    <span className="font-medium text-ink group-hover:text-teal transition-colors">
                      {n.title}
                    </span>
                    <ArrowRight className="w-4 h-4 text-teal shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {!news.length && <div className="pb-24" />}
    </article>
  );
}
